import "./App.css";

import { useEffect, lazy, Suspense, useState } from "react";
import { Home } from "./Pages/Home";
import { BiorSwap } from "./Pages/BiorSwap";
import {
  BrowserRouter as Router,
  Link,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { Header } from "./Pages/Header";
import { SideBar } from "./Modal";
import { IntegrateModal } from "./Pages/Components/IntegrateModal";
import { ProductProvider, useProduct } from "./context/ProductContext";
import { IntegrateModalProvider } from "./context/IntegrateModalContext";
import { getApiBase } from "./config/apiBase";
import { DownloadRedirect } from "./Pages/DownloadRedirect";

const Tos = lazy(() => import("./Pages/Tos.jsx").then((m) => ({ default: m.Tos })));
const NewFaq = lazy(() => import("./Pages/NewFaq.jsx").then((m) => ({ default: m.NewFaq })));
const Whitepaper = lazy(() => import("./Pages/Whitepaper.jsx").then((m) => ({ default: m.Whitepaper })));
const MediaKit = lazy(() => import("./Pages/MediaKit.jsx").then((m) => ({ default: m.MediaKit })));
const Cards = lazy(() => import("./Pages/Cards.jsx").then((m) => ({ default: m.Cards })));

let fallbackVisitorId = "";

function createVisitorId() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;

  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  return `visitor-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getLocalStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch (_) {
    return null;
  }
}

function readStoredVisitorId() {
  const storage = getLocalStorage();

  if (!storage) {
    return "";
  }

  try {
    return storage.getItem("visitor_id") || "";
  } catch (_) {
    return "";
  }
}

function persistVisitorId(visitorId) {
  const storage = getLocalStorage();

  if (!storage) {
    return;
  }

  try {
    storage.setItem("visitor_id", visitorId);
  } catch (_) {
    // Ignore storage write failures in Safari private mode and restricted browsers.
  }
}

function SiteLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  return (
    <>
      <Header onMenuOpen={() => setIsSidebarOpen(true)} />
      <Outlet />
      <SideBar show={isSidebarOpen} onHide={() => setIsSidebarOpen(false)} />
      <IntegrateModal />
    </>
  );
}

function Landing() {
  const { product } = useProduct();

  // Both panes are permanently mounted AND permanently painted (inactive
  // one is opacity:0 + position:absolute, not display:none). That's what
  // makes the cross-fade symmetric — neither direction has a first-mount
  // paint cost competing with the fade-in transition.
  return (
    <div className="landing-flip" data-testid="landing-flip-container" data-product={product}>
      <div className="landing-pane" data-active={product === "biorvault"} aria-hidden={product !== "biorvault"}>
        <Home />
      </div>
      <div className="landing-pane" data-active={product === "biorswap"} aria-hidden={product !== "biorswap"}>
        <BiorSwap />
      </div>
    </div>
  );
}

function NotFoundPage() {
  return (
    <main
      className="relative w-full overflow-hidden px-4 pb-24 sm:px-6 lg:px-8"
      style={{ paddingTop: "calc(8.75rem + var(--safe-area-top))" }}
    >
      <div className="mx-auto max-w-3xl rounded-[2rem] border border-white/10 bg-white/[0.04] p-8 text-center shadow-[0_24px_60px_rgba(6,6,34,0.32)] sm:p-10">
        <p className="font-dm text-xs uppercase tracking-[0.35em] text-white/35">404</p>
        <h1
          className="mt-5 font-syne text-white"
          style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)", lineHeight: "1.04", fontWeight: 700 }}
        >
          Page not found.
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-base leading-8 text-white/58 sm:text-lg">
          The page you asked for does not exist or may have moved. You can head back home or jump to the support pages from here.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.08] px-5 py-2.5 font-dm text-sm text-white transition-colors hover:bg-white/[0.12]"
          >
            Back home
          </Link>
          <Link
            to="/faq"
            className="inline-flex items-center rounded-full border border-white/10 bg-transparent px-5 py-2.5 font-dm text-sm text-white/78 transition-colors hover:bg-white/[0.06]"
          >
            FAQ
          </Link>
          <Link
            to="/tos"
            className="inline-flex items-center rounded-full border border-white/10 bg-transparent px-5 py-2.5 font-dm text-sm text-white/78 transition-colors hover:bg-white/[0.06]"
          >
            Terms of Service
          </Link>
        </div>
      </div>
    </main>
  );
}

// Download links are not site visits: the click log is their only record. A
// ping here would count the visit twice and save the first source as "direct".
function isDownloadLink() {
  return typeof window !== "undefined" && /^\/download(\/|$)/i.test(window.location.pathname);
}

function App() {
  useEffect(() => {
    if (isDownloadLink()) {
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);

    function getVisitorId() {
      const storedVisitorId = readStoredVisitorId();

      if (storedVisitorId) {
        fallbackVisitorId = storedVisitorId;
        return storedVisitorId;
      }

      if (!fallbackVisitorId) {
        fallbackVisitorId = createVisitorId();
      }

      persistVisitorId(fallbackVisitorId);
      return fallbackVisitorId;
    }

    // Where did this visit come from? Referrer + UTM tags only — these are
    // source LABELS, not identifiers, and no IP is sent or stored anywhere
    // (standing rule; see BiorAdmin-Backend CLAUDE.md).
    //
    // UTM is the load-bearing half. X (t.co) and TikTok's in-app browser
    // routinely strip or rewrite document.referrer, so untagged social traffic
    // is indistinguishable from direct — tagged links are what make the split
    // readable. The backend keeps only the referrer's HOSTNAME, because a full
    // referrer URL can carry personal data in its query string.
    function sourceParams() {
      try {
        const q = new URLSearchParams(window.location.search);
        return {
          referrer: document.referrer || undefined,
          utm_source: q.get("utm_source") || undefined,
          utm_medium: q.get("utm_medium") || undefined,
          utm_campaign: q.get("utm_campaign") || undefined,
        };
      } catch (_) {
        return {};
      }
    }

    const apiBase = getApiBase();
    if (!apiBase) {
      return () => controller.abort();
    }

    fetch(`${apiBase}/api/track-visit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visitorId: getVisitorId(), ...sourceParams() }),
      signal: controller.signal,
    })
      .then((res) => {
        // Surface non-2xx (CORS / 4xx / 5xx) in DevTools so failures aren't
        // silent. Local console only — no remote telemetry.
        if (!res.ok) {
          // eslint-disable-next-line no-console
          console.warn(`[track-visit] ${res.status} ${res.statusText}`);
        }
      })
      .catch((err) => {
        if (err && err.name !== "AbortError") {
          // eslint-disable-next-line no-console
          console.warn("[track-visit] request failed:", err.message || err);
        }
      })
      .finally(() => clearTimeout(timer));

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, []);

  return (
    <div
      style={{ overflowX: "clip" }}
      className="relative w-full min-h-screen overflow-x-clip"
    >
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <ProductProvider>
          <IntegrateModalProvider>
            <Routes>
              {/* Link shortener for stores: outside the site layout, before the catch-all. */}
              <Route path="download/:source" element={<DownloadRedirect />} />
              <Route element={<SiteLayout />}>
                <Route index element={<Landing />} />
                <Route path="tos/*" element={<Suspense fallback={null}><Tos /></Suspense>} />
                <Route path="faq/*" element={<Suspense fallback={null}><NewFaq /></Suspense>} />
                <Route path="whitepaper/*" element={<Suspense fallback={null}><Whitepaper /></Suspense>} />
                <Route path="mediakit" element={<Suspense fallback={null}><MediaKit /></Suspense>} />
                <Route path="cards" element={<Suspense fallback={null}><Cards /></Suspense>} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </IntegrateModalProvider>
        </ProductProvider>
      </Router>
    </div>
  );
}

export default App;
